import * as React from 'react';
import { useSafeState } from '../../../hooks/useSafeState';
import { useSellersPacketForm } from '../../../hooks/useSellersPacketForm';
import { useToggle } from '../../../hooks/useToggle';
import { FComponent } from '../../../types/common';
import { Packet, PacketQuestion } from '../../../types/packet.schema';
import { SellersPacket } from '../../../types/sellersPacket.schema';

import { Button } from '../../common/Button';
import {
  ProgressMessage,
  UploadProgressModal
} from '../../common/FileUpload/UploadProgressModal';
import { PacketFormQuestions } from '../../common/PacketForm/PacketFormQuestions';
import { Modal } from '../../common/Modal';
import { CaptchaToken, SliderCaptcha } from '../../unauthenticated/SliderCaptcha';
import { ErrorMessage } from '../../common/Form/ErrorMessage';
import { handleApiErrors } from '../../../utils';
import Badge from '@assets/images/icons/badge.svg';
import { logger } from '../../../lib/logger';
import ChevronRightIcon from '../../common/Icons/ChevronRightIcon';
import { SimpleProgressBar } from '../../common/SimpleProgressBar';
import { FrictionlessAuth, useFrictionlessAuth } from './FrictionlessAuth';
import { track } from '../../../lib/analytics';
const log = logger('FrictionlessPacket');

type FrictionlessPacketProps = {
  userEmail: string;
  packet: Packet;
  sellersPacket?: SellersPacket;
  referralCode: string;
  gcaptchaKey: string;
  onLoad?: (() => void) | (() => () => void);
  onSuccess?: (pubkey: string, isNewUser?: boolean) => void;
  pubkey: string; // For link questions when user is already signed up.
  pageSize?: number;
  externalId?: string;
  authToken?: string;
  authCopy?: Record<AuthState['state'], string>;
};

// 'signedIn': user was already signed into the site when landing. We won't need a password/captcha, we will need an authToken.
// 'authenticated': we already have an authToken either by logging in or did a token exchange from a url param
// 'login': user submitted their email and we found an already accepting account, the UI is now asking for a password.
// 'unauthenticated': user has not provided an email yet.
export type AuthState = {
  state: 'unauthenticated' | 'login' | 'authenticated' | 'signedIn';
  email: string;
  token: string;
  pubkey: string;
};

const FrictionlessPacket: FComponent<FrictionlessPacketProps> = ({
  packet,
  sellersPacket,
  userEmail,
  referralCode,
  gcaptchaKey,
  onLoad,
  onSuccess,
  pubkey = '',
  pageSize = 0,
  externalId,
  authToken,
  authCopy
}) => {
  const isExchange = Boolean(authToken);
  const onLoadRef = React.useRef(onLoad);
  const [previouslyAnsweredQuestionCount] = React.useState(() =>
    getQuestionCount(sellersPacket)
  );
  const pwdFieldRef = React.useRef<HTMLInputElement>(null);
  const [isCaptchaOpen, toggleCaptcha] = useToggle(false);
  const [isUploadProgressModalOpen, toggleUploadProgressModal] = useToggle(false);
  const [uploadProgress, setUploadProgress] = useSafeState<ProgressMessage>({
    percentFinished: 0,
    message: ''
  });

  const {
    isFormDisabled: isPacketIncomplete,
    submitSellersPacket,
    isLoadingSubmitSellersPacket,
    submitSellersPacketError,
    isSubmitSellersPacketError,
    ...questionFormProps
  } = useSellersPacketForm({
    packet,
    sellersPacket,
    isFrictionless: true
  });

  React.useEffect(() => {
    const cleanupFunction = onLoadRef.current?.();
    return cleanupFunction;
  }, []);

  const {
    authFormProps,
    checkEmail,
    login,
    register,
    captchaTokenRef,
    setCaptchaError,
    resetErrorState
  } = useFrictionlessAuth(userEmail, pubkey, authToken);
  const { auth, isAuthIncomplete, isAuthFormSubmitting, apiError, showApiError } =
    authFormProps;

  const { page, setPage, isPaged, isLastPage, isPageComplete, pageQuestions } =
    usePacketPaging(pageSize, packet, questionFormProps.errors);

  const isReadyToSubmit = !isPacketIncomplete && auth.state === 'authenticated';
  const isNextButton = !isReadyToSubmit && isPaged && !isLastPage;
  const isSubmitDisabled = !isPageComplete || isAuthIncomplete;
  // These are the important auth state transitions.
  // 1. unauthenticated -> login: we get a captcha token, then check if the email exists, if it does we transition to 'login' state and the UI asks for a password.
  // 2. unauthenticated -> authenticated: we get a captcha token, then check if the email exists, if it doesn't we register the client and get an auth token and pubkey.
  // 3. login -> authenticated: user provides a password, we already have a captcha token. We try to log user in, get an auth token and pubkey.
  // 4. signedIn -> authenticated: Also calls login() with their email and empty password to get a token and pubkey.
  const handleSubmit: React.MouseEventHandler<HTMLButtonElement> = async e => {
    e.preventDefault();
    resetErrorState();

    switch (auth.state) {
      // Already done with auth, save or publish as appropriate.
      case 'authenticated':
        submitPacket({ token: auth.token, pubkey: auth.pubkey });
        break;

      // transitions 1 and 2. See handleCaptureSuccess for the rest of these flows.
      case 'unauthenticated':
        toggleCaptcha();
        break;

      // transitions 3 and 4.
      case 'login':
      case 'signedIn':
        // We login whether the user landed in frictionless already signed in or provided an email and password.
        // If the user is already signed in, the login endpoint will return token and pubkey even when sending an empty password/captcha token.
        login(packet.id, referralCode, externalId)
          .then(submitPacket)
          .catch(handleApiErrors(log));
        break;

      default:
        throw new Error(`Invalid auth state: ${auth.state}`);
    }
  };

  const handleCaptchaSuccess = (token: CaptchaToken) => {
    captchaTokenRef.current = token;
    toggleCaptcha();
    checkEmail()
      .then(checkEmailResult => {
        if (checkEmailResult.message === 'emailExists') {
          pwdFieldRef.current.focus();
          return { token: null, pubkey: null } as const;
        } else {
          return register(packet.id, referralCode, externalId).then(res => {
            track('signup', ['tartle'], { isFrictionless: true });
            return res;
          });
        }
      })
      .then(({ token, pubkey } = { token: null, pubkey: null }) => {
        if (token && pubkey) {
          return submitPacket({ token, pubkey });
        }
      })
      .catch(handleApiErrors);
  };

  const submitPacket = ({ token, pubkey }: { token: string; pubkey: string }) =>
    submitSellersPacket({
      submitType: isPacketIncomplete ? 'save' : 'publish',
      setUploadProgress,
      toggleUploadProgressModal,
      authToken: token,
      pubkey: pubkey
    })
      .then(() => {
        if (isPacketIncomplete) {
          setPage(prev => prev + 1);
        } else {
          onSuccess?.(pubkey, true);
        }
      })
      .catch(error => {
        // We need to monitor if this happens as users will be stuck here.
        log.exception({
          error,
          tags: { pubkey, packetId: packet.id, referralCode }
        });
      });

  const handleCaptchaFailure = (message = "Could not verify you're not a robot") => {
    toggleCaptcha();
    setCaptchaError({ base: [message] });
  };

  const progress = getFormProgress(
    packet,
    previouslyAnsweredQuestionCount,
    questionFormProps.errors
  );

  return (
    <>
      <form className="packet-form">
        <div className="form-header">
          <div className="form-header-content">
            <div className="name-and-badge">
              <h1>{packet.name}</h1>
              <Badge />
            </div>
            {packet.description ? (
              <div className="description">{packet.description}</div>
            ) : null}

            {page > 0 || isExchange ? (
              <div className="progress-block">
                <SimpleProgressBar progress={progress} scaleFactor={1} />
              </div>
            ) : null}
          </div>
        </div>

        <PacketFormQuestions
          questions={pageQuestions}
          isIntegration={false}
          {...questionFormProps}
        />
        {page === 0 ? (
          <FrictionlessAuth
            ref={pwdFieldRef}
            isExchange={isExchange}
            authCopy={authCopy}
            {...authFormProps}
          />
        ) : null}
        <ErrorMessage
          errors={submitSellersPacketError || apiError}
          isShowing={isSubmitSellersPacketError || showApiError}
        />
        {isLastPage ? (
          <div className="end-block">
            <div className="end-block-content">
              <span role="img" aria-label="Finish Line">
                🏁
              </span>
              &nbsp;&nbsp;That&apos;s it! Click submit to finish and start earning.
            </div>
          </div>
        ) : null}
        <div className="button-bar">
          <Button
            isWaiting={isLoadingSubmitSellersPacket || isAuthFormSubmitting}
            className="publish-button"
            disabled={isSubmitDisabled}
            icon={<ChevronRightIcon />}
            postfix
            onClick={handleSubmit}>
            {isNextButton ? 'Next' : 'Submit'}
          </Button>
        </div>
      </form>

      <Modal
        className="dialog-custom"
        title=""
        isOpen={isCaptchaOpen}
        toggle={toggleCaptcha}
        isAnimated={false}>
        <SliderCaptcha
          onSuccess={handleCaptchaSuccess}
          onFailure={handleCaptchaFailure}
          gcaptchaKey={gcaptchaKey}
        />
      </Modal>
      <UploadProgressModal
        isOpen={isUploadProgressModalOpen}
        progress={uploadProgress}
        toggle={toggleUploadProgressModal}
      />
    </>
  );
};

export { FrictionlessPacket };

const hasMatchingErrors = (
  errors: Record<string, string[]>,
  questions: PacketQuestion[]
) => {
  const questionIdPattern = questions.map(question => question.id).join('|');
  const regex = new RegExp(`\\[${questionIdPattern}\\]`);
  return Object.keys(errors).some(key => regex.test(key));
};

const usePacketPaging = (
  pageSize: number,
  packet: Packet,
  formErrors: Record<string, string[]>
) => {
  const [page, setPage] = React.useState(0);
  const pageStartIndex = page * pageSize;
  const pageQuestions = pageSize
    ? packet.questions.slice(pageStartIndex, pageStartIndex + pageSize)
    : packet.questions;
  const isPageComplete = !hasMatchingErrors(formErrors, pageQuestions);
  const isLastPage = pageStartIndex + pageSize >= packet.questions.length;

  return {
    page,
    setPage,
    isPaged: pageSize > 0,
    isLastPage,
    isPageComplete,
    pageQuestions
  };
};

const getQuestionCount = (sellersPacket: SellersPacket) => {
  if (!sellersPacket) {
    return 0;
  }
  return new Set(sellersPacket.answers.map(answer => answer.questionId)).size;
};

const getFormProgress = (
  packet: Packet,
  previouslyAnsweredQuestionCount: number,
  errors: Record<string, string[]>
) => {
  const totalQuestionCount = packet.questions.length + previouslyAnsweredQuestionCount;
  return ((totalQuestionCount - Object.keys(errors).length) / totalQuestionCount) * 100;
};
